"use client";

import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import { useState } from "react";

import { Container } from "@/app/(marketing)/_components/ui/container";
import { api } from "@/lib/trpc/react";

type VerifyStatus = "verified" | "unknown" | "invalid" | "unavailable";

function ResultCard({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/20 bg-white/10 px-5 py-4 text-left backdrop-blur-sm">
      {icon}
      <div className="text-sm text-white sm:text-base">{children}</div>
    </div>
  );
}

function VerifyResult({
  status,
  domain,
  onRetry,
}: {
  status: VerifyStatus;
  domain: string;
  onRetry: () => void;
}) {
  switch (status) {
    case "invalid":
      return (
        <p className="text-sm text-white/70">
          That doesn&apos;t look like a domain. Try something like{" "}
          <span className="font-medium text-white">teracanalytics.com</span>.
        </p>
      );

    case "verified":
      return (
        <ResultCard
          icon={
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />
          }
        >
          <span className="font-semibold">{domain}</span> is an official Terac
          sending domain. This email is genuinely from us.
        </ResultCard>
      );

    case "unknown":
      return (
        <ResultCard
          icon={
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
          }
        >
          We don&apos;t recognize{" "}
          <span className="font-semibold">{domain}</span> as a Terac domain. Be
          careful, a genuine Terac email always links to terac.com.
        </ResultCard>
      );

    // We never reached the sending-domain list, so we have no verdict at all.
    // Say that, and keep the one piece of advice that still holds without one,
    // rather than borrowing the "we don't recognize this" copy, which would
    // read as a finding about the domain that we did not actually make.
    case "unavailable":
      return (
        <ResultCard
          icon={<RefreshCw className="mt-0.5 h-5 w-5 shrink-0 text-white/70" />}
        >
          <p>
            We couldn&apos;t check{" "}
            <span className="font-semibold">{domain}</span> just now, so this
            isn&apos;t a judgement about it either way. A genuine Terac email
            always links to terac.com.
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-white underline underline-offset-4 hover:opacity-80"
          >
            Try again
          </button>
        </ResultCard>
      );

    default: {
      // A status added later must bring its own branch. Falling through to
      // the `unknown` card would warn someone off a domain we never checked,
      // which is the failure this component was corrected for.
      status satisfies never;
      return null;
    }
  }
}

export default function VerifyHero({
  initialDomain,
}: {
  initialDomain: string;
}) {
  const [value, setValue] = useState(initialDomain);
  // Seeded from ?domain= so a link like /verify?domain=teracanalytics.com checks
  // immediately, no typing needed.
  const [submitted, setSubmitted] = useState(initialDomain.trim());

  const { data, isFetching, refetch } =
    api.outboundCampaign.verifyDomain.useQuery(
      { domain: submitted },
      { enabled: Boolean(submitted), staleTime: 5 * 60 * 1000 },
    );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(value.trim());
  };

  // Re-submitting the same domain reuses the query key and the cached answer,
  // so an `unavailable` card has to refetch explicitly to offer a real retry.
  const handleRetry = () => {
    void refetch();
  };

  return (
    <section className="relative px-5 pt-16 pb-28 md:px-8 md:pt-20 md:pb-32 lg:px-14">
      <Container>
        <div className="mx-auto flex w-full max-w-[820px] flex-col items-center gap-8">
          <div className="flex flex-col items-center gap-5 text-center text-white">
            <h1 className="text-heading-40 sm:text-heading-48 md:text-heading-56 max-w-[720px] text-balance">
              Verify a Terac domain
            </h1>
            <p className="max-w-[620px] text-base text-balance text-white/80 md:text-lg">
              Got an email from Terac and want to be sure it&apos;s us? We reach
              out from many domains. Paste the sender&apos;s domain below and
              we&apos;ll tell you whether it&apos;s genuinely one of ours.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex w-full max-w-xl flex-col gap-2 rounded-2xl border border-white/15 bg-white p-2 sm:flex-row"
          >
            <input
              type="text"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
              }}
              placeholder="e.g. teracanalytics.com"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              aria-label="Sender domain to verify"
              className="text-marketing-blue placeholder:text-marketing-blue/40 h-12 flex-1 rounded-xl bg-transparent px-4 text-base outline-none"
            />
            <button
              type="submit"
              className="bg-marketing-blue inline-flex h-12 items-center justify-center gap-2 rounded-xl px-8 text-base font-medium text-white transition-colors hover:opacity-90"
            >
              Check
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          <div className="min-h-[2rem] w-full max-w-xl" aria-live="polite">
            {Boolean(submitted) && isFetching ? (
              <div className="flex items-center justify-center gap-2 text-white/80">
                <Loader2 className="h-4 w-4 animate-spin" />
                Checking {submitted}
                &hellip;
              </div>
            ) : data ? (
              <VerifyResult
                status={data.status}
                domain={data.domain ?? submitted}
                onRetry={handleRetry}
              />
            ) : null}
          </div>
        </div>
      </Container>
    </section>
  );
}
